import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, FileText, Eye, Loader, History, RotateCcw, ChevronDown, ChevronUp, Search, Code, Type } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';
import { pages as pagesApi, uploads } from '../../services/api/admin';

import { ImagePicker, PageTitle, HtmlContentEditor } from './shared';

const PageBuilder = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editorMode, setEditorMode] = useState(null); // 'wysiwyg' | 'code' | null
  // Version history state
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionPreviewOpen, setVersionPreviewOpen] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);
  const [seoSectionOpen, setSeoSectionOpen] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    subtitle: '',
    hero_image_url: '',
    content: '',
    is_published: true,
    // SEO & Social Sharing fields
    meta_description: '',
    page_type: '',
    keywords: '',
    canonical_url: '',
    no_index: false,
    og_title: '',
    og_description: '',
    og_image_url: '',
    twitter_card_type: 'summary_large_image'
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pages`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async (pageId) => {
    setLoadingVersions(true);
    setVersions([]);
    try {
      const data = await pagesApi.getVersions(pageId);
      setVersions(data || []);
    } catch (error) {
      console.error('Failed to load versions:', error);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!window.confirm(`Are you sure you want to restore to version ${version.version_number}? Your current content will be saved as a new version first.`)) {
      return;
    }

    setRestoringVersion(true);
    try {
      const restored = await pagesApi.restore(editing, version.id);
      setFormData({
        slug: restored.slug,
        title: restored.title,
        subtitle: restored.subtitle || '',
        hero_image_url: restored.hero_image_url || '',
        content: restored.content,
        is_published: restored.is_published,
        // SEO & Social Sharing fields
        meta_description: restored.meta_description || '',
        page_type: restored.page_type || '',
        keywords: restored.keywords || '',
        canonical_url: restored.canonical_url || '',
        no_index: restored.no_index || false,
        og_title: restored.og_title || '',
        og_description: restored.og_description || '',
        og_image_url: restored.og_image_url || '',
        twitter_card_type: restored.twitter_card_type || 'summary_large_image'
      });
      setVersionPreviewOpen(false);
      setSelectedVersion(null);
      setMessage('✅ Page restored to previous version!');
      fetchVersions(editing); // Refresh version list
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Restore error:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setRestoringVersion(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: editing ? formData.slug : generateSlug(title)
    });
  };

  // Generic upload handler for WYSIWYG editor images
  const handleEditorImageUpload = async (file) => {
    try {
      const data = await uploads.uploadImage(file, { autoGallery: true, context: 'pages' });
      return data.url;
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  const handleSave = async () => {
    if (!formData.slug || !formData.title || !formData.content) {
      setMessage('❌ Please provide slug, title, and content');
      return;
    }

    try {
      const url = editing
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/pages/${editing}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/pages`;

      const method = editing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          subtitle: formData.subtitle || null,
          hero_image_url: formData.hero_image_url || null,
          // SEO fields - convert empty strings to null
          meta_description: formData.meta_description || null,
          page_type: formData.page_type || null,
          keywords: formData.keywords || null,
          canonical_url: formData.canonical_url || null,
          og_title: formData.og_title || null,
          og_description: formData.og_description || null,
          og_image_url: formData.og_image_url || null,
          twitter_card_type: formData.twitter_card_type || 'summary_large_image'
        })
      });

      if (response.ok) {
        setMessage('✅ Page saved successfully!');
        setModalOpen(false);
        setEditing(null);
        setFormData({
          slug: '', title: '', subtitle: '', hero_image_url: '', content: '', is_published: true,
          meta_description: '', page_type: '', keywords: '', canonical_url: '', no_index: false,
          og_title: '', og_description: '', og_image_url: '', twitter_card_type: 'summary_large_image'
        });
        fetchPages();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to save page'}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const handleEdit = (page) => {
    setEditing(page.id);
    setFormData({
      slug: page.slug,
      title: page.title,
      subtitle: page.subtitle || '',
      hero_image_url: page.hero_image_url || '',
      content: page.content,
      is_published: page.is_published,
      // SEO & Social Sharing fields
      meta_description: page.meta_description || '',
      page_type: page.page_type || '',
      keywords: page.keywords || '',
      canonical_url: page.canonical_url || '',
      no_index: page.no_index || false,
      og_title: page.og_title || '',
      og_description: page.og_description || '',
      og_image_url: page.og_image_url || '',
      twitter_card_type: page.twitter_card_type || 'summary_large_image'
    });
    setEditorMode(null); // Reset editor mode to show selection
    setModalOpen(true);
    fetchVersions(page.id); // Fetch versions when editing
  };

  const handleCreateNew = () => {
    setEditing(null);
    setFormData({
      slug: '', title: '', subtitle: '', hero_image_url: '', content: '', is_published: true,
      meta_description: '', page_type: '', keywords: '', canonical_url: '', no_index: false,
      og_title: '', og_description: '', og_image_url: '', twitter_card_type: 'summary_large_image'
    });
    setSeoSectionOpen(false);
    setEditorMode(null); // Reset editor mode to show selection
    setModalOpen(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pages/${pageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('✅ Page deleted successfully!');
        fetchPages();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to delete page'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const handleModalClose = (open) => {
    if (!open) {
      setEditing(null);
      setFormData({
        slug: '', title: '', subtitle: '', hero_image_url: '', content: '', is_published: true,
        meta_description: '', page_type: '', keywords: '', canonical_url: '', no_index: false,
        og_title: '', og_description: '', og_image_url: '', twitter_card_type: 'summary_large_image'
      });
      setVersions([]);
      setSelectedVersion(null);
      setVersionPreviewOpen(false);
      setSeoSectionOpen(false);
      setEditorMode(null); // Reset editor mode
    }
    setModalOpen(open);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Custom Pages"
        description="Create and manage custom pages with WYSIWYG editor"
        icon={<FileText className="h-6 w-6" />}
        actions={
          <Button onClick={handleCreateNew} variant="adminPrimary">
            <Plus className="h-4 w-4 mr-2" />
            Create New Page
          </Button>
        }
      />

      {message && (
        <div className={`p-4 rounded ${
          message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' :
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Pages List */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Existing Pages ({pages.length})</h3>

        {pages.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No custom pages yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Create your first page to get started</p>
            <Button onClick={handleCreateNew} variant="adminPrimary">
              <Plus className="h-4 w-4 mr-2" />
              Create New Page
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            {pages.map((page) => (
              <Card key={page.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">{page.title}</h4>
                    <p className="text-sm text-blue-600 font-mono bg-blue-50 px-2 py-1 rounded inline-block mt-1">
                      /page/{page.slug}
                    </p>
                    {page.subtitle && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">{page.subtitle}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    page.is_published ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {page.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <div className="w-full h-32 bg-gray-100 dark:bg-gray-800 rounded mb-3 overflow-hidden flex items-center justify-center">
                  {page.hero_image_url ? (
                    <img
                      src={page.hero_image_url}
                      alt={page.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 dark:text-gray-400" />
                  )}
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(page)} className="flex-1">
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`https://owazamartialarts.com/page/${page.slug}`, '_blank')}
                    className="flex-1"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Preview
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(page.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Page Editor Modal */}
      <Dialog open={modalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              {editing ? (
                <>
                  <Edit2 className="h-5 w-5 mr-2 text-red-600" />
                  Edit Page
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 mr-2 text-red-600" />
                  Create New Page
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Editor Selection Screen */}
            {editorMode === null ? (
              <div className="py-8">
                <h3 className="text-lg font-semibold text-center mb-2">Choose Your Editor</h3>
                <p className="text-sm text-gray-600 text-center mb-8">
                  Select how you want to edit your page content
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {/* Simple Editor Option */}
                  <button
                    type="button"
                    onClick={() => setEditorMode('wysiwyg')}
                    className="group p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="flex items-center justify-center mb-4">
                      <div className="p-4 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                        <Type className="h-8 w-8 text-blue-600" />
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-center mb-2">Simple Editor</h4>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Visual editor with formatting toolbar
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>Easy to use, like a word processor</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>Best for basic text and images</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>No coding knowledge required</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✗</span>
                        <span>Limited layout options</span>
                      </li>
                    </ul>
                  </button>

                  {/* Advanced Editor Option */}
                  <button
                    type="button"
                    onClick={() => setEditorMode('code')}
                    className="group p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  >
                    <div className="flex items-center justify-center mb-4">
                      <div className="p-4 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                        <Code className="h-8 w-8 text-purple-600" />
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-center mb-2">Advanced Editor</h4>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Full HTML/CSS code editor
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>Complete layout control</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>Custom CSS Grid & Flexbox layouts</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>Complex multi-column designs</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-600 mr-2">⚠</span>
                        <span>Requires HTML/CSS knowledge</span>
                      </li>
                    </ul>
                  </button>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-xs text-gray-500 max-w-2xl mx-auto">
                    <strong>Tip:</strong> The simple editor will strip complex HTML if you switch back from the advanced editor.
                    Choose the advanced editor if you need custom layouts or styling.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Page Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="e.g., About Us"
                    />
                  </div>
                  <div>
                    <Label>URL Slug *</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="e.g., about-us"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Page URL: <span className="font-mono text-blue-600">/page/{formData.slug || 'your-slug'}</span>
                    </p>
                  </div>
                </div>

                {/* Subtitle */}
                <div>
                  <Label>Subtitle (Hero Section)</Label>
                  <Textarea
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Optional tagline or description"
                    rows={2}
                  />
                </div>

                {/* Hero Image */}
                <ImagePicker
                  value={formData.hero_image_url}
                  onChange={(url) => setFormData({ ...formData, hero_image_url: url })}
                  context="pages"
                  label="Hero Background Image"
                  previewSize="md"
                />

                {/* Content Editor */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Page Content *</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditorMode(null)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Switch Editor Mode
                    </Button>
                  </div>
                  <HtmlContentEditor
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Write your page content here..."
                    onImageUpload={handleEditorImageUpload}
                    defaultMode={editorMode}
                    showModeChooser={false}
                    pageId={editing || 'new'}
                  />
                </div>

                {/* SEO & Social Sharing Section */}
                <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setSeoSectionOpen(!seoSectionOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">SEO & Social Sharing</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">(optional)</span>
                </div>
                {seoSectionOpen ? (
                  <ChevronUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                )}
              </button>

              {seoSectionOpen && (
                <div className="p-4 space-y-6 border-t">
                  {/* Basic SEO */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Basic SEO</h4>

                    <div>
                      <Label>Meta Description</Label>
                      <Textarea
                        value={formData.meta_description}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                        placeholder="Describe this page for search engines (150-160 characters ideal)"
                        rows={3}
                        maxLength={300}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.meta_description.length}/160 characters (recommended)
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Page Type</Label>
                        <Select
                          value={formData.page_type || ''}
                          onValueChange={(value) => setFormData({ ...formData, page_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select page type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="about">About</SelectItem>
                            <SelectItem value="bio">Bio / Profile</SelectItem>
                            <SelectItem value="policy">Policy</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                            <SelectItem value="article">Article</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">Affects structured data schema</p>
                      </div>

                      <div>
                        <Label>Keywords</Label>
                        <Input
                          value={formData.keywords}
                          onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                          placeholder="martial arts, jiu-jitsu, spring hill"
                        />
                        <p className="text-xs text-gray-500 mt-1">Comma-separated</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Canonical URL Override</Label>
                        <Input
                          value={formData.canonical_url}
                          onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
                          placeholder="/page/original-url"
                        />
                        <p className="text-xs text-gray-500 mt-1">Only if this page duplicates another</p>
                      </div>

                      <div className="flex items-end pb-1">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={formData.no_index}
                            onCheckedChange={(checked) => setFormData({ ...formData, no_index: checked })}
                          />
                          <Label>Hide from search engines (noindex)</Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Open Graph */}
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Open Graph (Facebook, LinkedIn)</h4>

                    <div>
                      <Label>OG Title Override</Label>
                      <Input
                        value={formData.og_title}
                        onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                        placeholder="Custom title for social shares (defaults to page title)"
                      />
                    </div>

                    <div>
                      <Label>OG Description Override</Label>
                      <Textarea
                        value={formData.og_description}
                        onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                        placeholder="Custom description for social shares (defaults to meta description)"
                        rows={2}
                      />
                    </div>

                    <ImagePicker
                      value={formData.og_image_url}
                      onChange={(url) => setFormData({ ...formData, og_image_url: url })}
                      context="pages"
                      label="Social Sharing Image (Recommended: 1200x630px)"
                      previewSize="md"
                    />
                    <p className="text-xs text-gray-500 mt-1">Defaults to hero image if not set</p>
                  </div>

                  {/* Twitter */}
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Twitter Card</h4>

                    <div className="max-w-xs">
                      <Label>Card Type</Label>
                      <Select
                        value={formData.twitter_card_type}
                        onValueChange={(value) => setFormData({ ...formData, twitter_card_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="summary_large_image">Large Image (recommended)</SelectItem>
                          <SelectItem value="summary">Summary (small image)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                )}
                </div>

                {/* Published Toggle */}
                <div className="flex items-center space-x-2 pt-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
                  <Label>Published (visible on website)</Label>
                </div>

                {/* Version History Section */}
                {editing && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center text-base font-medium">
                    <History className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                    Version History
                  </Label>
                  {loadingVersions && <Loader className="h-4 w-4 animate-spin text-gray-400" />}
                </div>

                {versions.length === 0 && !loadingVersions ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No previous versions available. Versions are created automatically when you save changes.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {versions.map((version) => (
                      <div
                        key={version.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">Version {version.version_number}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(version.created_at).toLocaleString()}
                            </span>
                          </div>
                          {version.changed_by && (
                            <p className="text-xs text-gray-400 mt-0.5">by {version.changed_by}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedVersion(version);
                            setVersionPreviewOpen(true);
                          }}
                          className="text-xs"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                    ))}
                      </div>
                    )}
                  </div>
                )}

                    {/* Version Preview Modal */}
                <Dialog open={versionPreviewOpen} onOpenChange={setVersionPreviewOpen}>
              <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <History className="h-5 w-5 mr-2 text-blue-600" />
                    Version {selectedVersion?.version_number} Preview
                  </DialogTitle>
                </DialogHeader>

                {selectedVersion && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <h4 className="font-semibold">{selectedVersion.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Saved on {new Date(selectedVersion.created_at).toLocaleString()}
                        {selectedVersion.changed_by && ` by ${selectedVersion.changed_by}`}
                      </p>
                    </div>

                    {/* Content Preview */}
                    <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto bg-background">
                      <Label className="text-sm font-medium mb-2 block text-gray-600 dark:text-gray-400">Content Preview</Label>
                      <div
                        className="prose prose-sm max-w-none custom-page-content"
                        dangerouslySetInnerHTML={{ __html: selectedVersion.content }}
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button variant="outline" onClick={() => setVersionPreviewOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleRestoreVersion(selectedVersion)}
                        disabled={restoringVersion}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md"
                      >
                        {restoringVersion ? (
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-2" />
                        )}
                        Restore This Version
                      </Button>
                    </div>
                  </div>
                )}
                  </DialogContent>
                </Dialog>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSave} variant="adminGold">
                <Save className="h-4 w-4 mr-2" />
                {editing ? 'Update Page' : 'Create Page'}
              </Button>
              <Button onClick={() => handleModalClose(false)} variant="adminOutline">
                <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilder;
